/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { Theme } from "@material-ui/core";
import grey from "@material-ui/core/colors/grey";
import { createStyles, WithStyles, withStyles } from "@material-ui/styles";
import React from "react";
import { connect, useSelector } from "react-redux";
import { Redirect, Route, Switch } from "react-router";
import { HashRouter as Router } from "react-router-dom";
import { Dispatch } from "redux";
import "./App.css";
import EvalEditor from "./react/components/EvalEditor";
import EvalList from "./react/components/EvalList";
import AppOverlay from "./react/components/AppOverlay";
import { Card } from './react/components/form/components/Card';
import { DataImport } from './react/components/form/components/DataImport';
import { DatePicker } from './react/components/form/components/DatePicker';
import { Dismissible } from './react/components/form/components/Dismissible';
import { ErrorBlock } from './react/components/form/components/ErrorBlock';
import { Footnote } from './react/components/form/components/Footnote';
import { ImageGallery } from './react/components/form/components/ImageGallery';
import { ImageImport } from './react/components/form/components/ImageImport';
import { FileImport } from './react/components/form/components/FileImport';
import { Label } from './react/components/form/components/Label';
import { List } from './react/components/form/components/List';
import { SignatureCapture } from './react/components/form/components/SignatureCapture';
import { Subform } from './react/components/form/components/Subform';
import { Tally } from './react/components/form/components/Tally';
import { TextField } from './react/components/form/components/TextField';
import { View } from './react/components/form/components/View';
import { registry } from './react/components/form/registry';
import HelpPage from "./react/components/HelpPage";
import Home from "./react/components/Home";
import { MultiCheckbox } from "./react/components/MultiCheckbox";
import { Checkbox } from "./react/components/form/components/Checkbox";
import PDFRender from "./react/components/PDFRender";
import { Radio } from "./react/components/Radio";
import AppFooter from './react/container/AppFooter';
import AppHeader from "./react/container/AppHeader";
import FollowUpTab from "./react/container/FollowUpTab";
import LoginPage from "./react/container/LoginPage";
import UpdateAppDialog from "./react/container/UpdateAppDialog";
import { AppState } from "./redux";
import { signOutAction } from "./redux/session/actions";
import WmpList from "./react/components/WmpList";
import WmpAuditList from "./react/components/WmpAuditList";
import { selectCertifications } from './react/components/NewEvalButton';

import { createSelector } from 'reselect';
import { RufasInputs } from "./react/components/form/components/RufasInputs";
import { Table } from './react/components/form/components/Table';
import { Alert } from './react/components/form/components/Alert';
import { Appearable } from './react/components/form/components/Appearable';
import { Calendar } from "./react/components/form/components/Calendar";
import { YearDropdown } from "./react/components/form/components/YearDropdown";
import { CollapsiblePanel } from './react/components/form/components/CollapsiblePanel';
import { Image } from './react/components/form/components/Image';
import { Modal } from "./react/components/form/components/Modal";

const styles = (theme: Theme) => {
  return createStyles({
    root: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      zIndex: 0,
      backgroundColor: theme.palette.background.default,
      height: "100vh",
    },
    errorNotification: {
      flex: 1,
      display: "flex",
      paddingTop: "200px",
    },
    header: {
    },
    body: {
      flex: 1,
      overflow: "hidden",
      display: "flex",
    },

    statusBar: {
      bottom: 0,
      width: "100%",
      backgroundColor: grey[50],
      height: "20px",
      borderTopColor: grey[300],
      borderTopWidth: "1px",
      borderTopStyle: "solid",
    },
    statusItem: {
      marginLeft: theme.spacing(1),
    },
    version: {
      position: "absolute",
      bottom: 0,
      right: theme.spacing(5),
    },
  });
};

interface AppProps extends WithStyles<typeof styles> {
  userId: string | null;
  isAuthenticated: boolean;
  isAuthorized?: boolean;
  isSigningIn: boolean;
  isSyncing: boolean;
  error?: Error;
  onSignOut: () => void;
}

registry.registerComponent('Label', Label);
registry.registerComponent('MultiCheckbox', MultiCheckbox as any);
registry.registerComponent('Checkbox', Checkbox as any);
registry.registerComponent('Card', Card);
registry.registerComponent('Label', Label);
registry.registerComponent('DateTimePicker', DatePicker);
registry.registerComponent('TextField', TextField);
registry.registerComponent('List', List);
registry.registerComponent('View', View);
registry.registerComponent('DataImport', DataImport);
registry.registerComponent('Dismissible', Dismissible);
registry.registerComponent('Appearable', Appearable);
registry.registerComponent('ImageGallery', ImageGallery);
registry.registerComponent('ImageImport', ImageImport);
registry.registerComponent('FileImport', FileImport);
registry.registerComponent('Signature', SignatureCapture);
registry.registerComponent('Tally', Tally);
registry.registerComponent('Subform', Subform);
registry.registerComponent('Footnote', Footnote as any);
registry.registerComponent('Radio', Radio);
registry.registerComponent('RufasInputs', RufasInputs);
registry.registerComponent('Table', Table);
registry.registerComponent('Alert', Alert);
registry.registerComponent('YearDropdown', YearDropdown);
registry.registerComponent('Calendar', Calendar);
registry.registerComponent('CollapsiblePanel', CollapsiblePanel);
registry.registerComponent('Image', Image);
registry.registerComponent('Modal', Modal);

export const canViewEvalType = (type: string) => createSelector(
  selectCertifications,
  certs => {
    if (!certs) {
      return false;
    }
    const certsForType = certs[type];
    if (certsForType) {
      if (Array.isArray(certsForType)) {
        if ((certsForType as unknown as string[]).findIndex(status => status !== 'expired') >= 0) {
          return true;
        }
      } else if ((certsForType as any)?.allow_create) {
        return true;
      }
    }
    return false;
  }
);

function App(props: AppProps & { passphrase?: string }) {

  const { classes, userId, onSignOut, isAuthenticated, isSyncing } = props;
  const canViewWmps = useSelector(canViewEvalType('wmp_audit'));
  
    return (
      <Router>
        <div className={["App", classes.root].join(" ")}>

          <UpdateAppDialog></UpdateAppDialog>

          <div className={classes.header}>
            <Route
              render={props =>
                userId !== null ? <AppHeader userId={userId} onSignOut={onSignOut} {...props} /> : null
              }
            />
          </div>
          <AppOverlay></AppOverlay>
          <div className={classes.body}>
            <Route
              render={routeProps => {
                // check authN
                const shouldRedirect =
                  !isAuthenticated && routeProps.location.pathname !== "/" && routeProps.location.pathname !== "/login";

                if (shouldRedirect) return <Redirect to="/login" />;

                // check authZ
                // if (this.props.isAuthorized === false) {
                if (props.isAuthorized === false) {

                  return (
                    <div className={classes.errorNotification}>
                      <ErrorBlock
                        name="Unauthorized"
                        message="Your account is not authorized to use the FARM evaluations app. Only accounts with a current, unexpired certification or actively participating in a training session are permitted use of this resource."
                      />
                    </div>
                  );
                }

                return (
                  <Switch>
                    <Route path="/login" component={LoginPage} />
                    <Route
                      exact
                      path="/"
                      render={props => <Redirect to={props.location.search.includes("code") ? "/oauth" : "/home"} />}
                    />
                    <Route path="/home" component={Home} />
                    <Route exact path="/evals/:evalId" component={EvalEditor} />
                    <Route exact path="/evals" component={EvalList} />
                    <Route path="/follow-ups" component={FollowUpTab} />
                    {
                      canViewWmps ?
                        <React.Fragment>
                          <Route exact path="/wmps" component={WmpList} />
                          <Route exact path="/wmps/:wmpId" component={WmpAuditList} />
                        </React.Fragment> : null
                    }
                    <Route path="/help" component={HelpPage} />
                    <Route path="/preview/:evalId" component={PDFRender} />
                  </Switch>
                );
              }}
            />
          </div>

          <AppFooter isSyncing={isSyncing}></AppFooter>
        </div>
      </Router>
    );
}

const mapStateToProps = (state: AppState) => ({
  userId: state.session.dfdmToken ? state.session.dfdmToken.sub : null,
  isSigningIn: state.session.isSigningIn,
  error: state.session.error,
  isAuthenticated: state.session.dfdmToken !== null,
  isAuthorized: state.session.isAuthorized,
  isSyncing: state.evals.isFetching,
});
const mapDispatchToProps = (dispatch: Dispatch) => ({
  onSignOut: () => dispatch(signOutAction()),
});

export default withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(App));